import manifestJson from '../../../../public/dashboard-assets/manifest.json';

// Generated dashboards are untrusted documents, never application-origin code.
// The caller must also use sandbox="allow-scripts" (without allow-same-origin).
const CONTENT_SECURITY_POLICY =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'";

export type DashboardAsset = {
  type: 'script' | 'font';
  file: string;
  integrity: string;
  family?: string;
  weight?: string;
  /** Licence and notice files shipped next to the asset in exports. */
  licenses?: string[];
};

export type DashboardData = {
  title?: unknown;
  generatedAt?: unknown;
  sources?: unknown;
  guidelines?: unknown;
};

// Pinned copies served from public/dashboard-assets/ (scripts/dashboard-assets.mjs).
const DASHBOARD_ASSETS = (manifestJson as { assets: Record<string, DashboardAsset> }).assets;

export const DASHBOARD_ASSETS_PATH = '/dashboard-assets/';

export const contentSecurityPolicy = ({
  scriptSrc = [] as string[],
  fontSrc = [] as string[],
} = {}) =>
  CONTENT_SECURITY_POLICY.replace(
    "script-src 'unsafe-inline'",
    ["script-src 'unsafe-inline'", ...scriptSrc].join(' ')
  ).replace(
    "font-src 'none'",
    fontSrc.length ? `font-src ${fontSrc.join(' ')}` : "font-src 'none'"
  );

/**
 * Known assets named by `<meta name="klax-assets" content="echarts@6 inter@5">`.
 * Dashboards declare names, never URLs; unknown names are ignored.
 */
export const declaredDashboardAssets = (document: Document): DashboardAsset[] => {
  const names = new Set(
    Array.from(document.querySelectorAll('meta[name="klax-assets"]')).flatMap(
      (meta) => meta.getAttribute('content')?.split(/\s+/) ?? []
    )
  );
  return Array.from(names)
    .filter((name) => Object.prototype.hasOwnProperty.call(DASHBOARD_ASSETS, name))
    .map((name) => DASHBOARD_ASSETS[name]);
};

/** The `klax-dashboard-data` JSON block, or null when missing or invalid. */
export const dashboardData = (document: Document): DashboardData | null => {
  const block = document.getElementById('klax-dashboard-data');
  if (!block?.textContent) return null;
  try {
    const data: unknown = JSON.parse(block.textContent);
    return data && typeof data === 'object' && !Array.isArray(data)
      ? (data as DashboardData)
      : null;
  } catch {
    return null;
  }
};

/** Data block `title`, else `<title>`, else an empty string. */
export const dashboardTitleOf = (document: Document) => {
  const data = dashboardData(document);
  const title = typeof data?.title === 'string' ? data.title.trim() : '';
  return title || document.title.trim();
};

export const dashboardTitle = (source: string) =>
  dashboardTitleOf(new DOMParser().parseFromString(source, 'text/html'));

export const fontFaceRule =(asset: DashboardAsset, url: string) =>
  `@font-face{font-family:${asset.family};src:url(${url}) format("woff2");font-weight:${asset.weight};font-display:swap}`;

/**
 * ECharts keeps its hidden tooltip box where it was last shown. After the panel narrows,
 * that invisible box widened the page; chart containers clip it (tooltips are confined).
 */
export const ECHARTS_CONTAINER_RULE = '[_echarts_instance_]{overflow:hidden}';

/** Platform styles for the declared libraries, inserted before the dashboard's own CSS. */
export const libraryStyles = (document: Document, assets: DashboardAsset[]) =>
  assets.includes(DASHBOARD_ASSETS['echarts@6'])
    ? [Object.assign(document.createElement('style'), { textContent: ECHARTS_CONTAINER_RULE })]
    : [];

/** Parses the source, reads the asset declaration, then removes navigation and embedding markup. */
export const sanitizeDashboard = (source: string) => {
  const document = new DOMParser().parseFromString(source, 'text/html');
  // Read before sanitising: the declaration is a <meta>, which is removed below.
  const assets = declaredDashboardAssets(document);
  document
    .querySelectorAll('base, meta, iframe, frame, object, embed, form, link')
    .forEach((node) => node.remove());
  // In-document fragments (anchors, SVG <use>) stay; anything that can leave the document goes.
  document.querySelectorAll('[href]').forEach((node) => {
    if (!node.getAttribute('href')?.startsWith('#')) node.removeAttribute('href');
  });
  return { document, assets };
};

export const prependPolicy = (document: Document, content: string) => {
  const policy = document.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  policy.content = content;
  document.head.prepend(policy);
};

export const serializeDashboard = (document: Document) =>
  '<!doctype html>' + document.documentElement.outerHTML;

export const dashboardDocument = (source: string, { preview = false } = {}): string => {
  const { document, assets } = sanitizeDashboard(source);
  // Assets come only from this KLAX environment's own origin; the sandboxed frame is opaque.
  const { origin } = window.location;
  const assetUrl = (asset: DashboardAsset) => `${origin}${DASHBOARD_ASSETS_PATH}${asset.file}`;
  const scripts = assets.filter((asset) => asset.type === 'script');
  const fonts = assets.filter((asset) => asset.type === 'font');
  // Injected after the policy, before any dashboard markup, so libraries load first.
  const injected = [
    ...fonts.map((font) => {
      const style = document.createElement('style');
      style.textContent = fontFaceRule(font, assetUrl(font));
      return style;
    }),
    ...libraryStyles(document, assets),
    ...scripts.map((asset) => {
      const script = document.createElement('script');
      script.setAttribute('src', assetUrl(asset));
      script.setAttribute('integrity', asset.integrity);
      script.setAttribute('crossorigin', 'anonymous');
      return script;
    }),
  ];
  document.head.prepend(...injected);
  prependPolicy(
    document,
    contentSecurityPolicy({ scriptSrc: scripts.map(assetUrl), fontSrc: fonts.map(assetUrl) })
  );
  const style = document.createElement('style');
  // Gallery thumbnails are scaled down; inner scrollbars would only add noise.
  // The open preview keeps a 4px bar so the document can still scroll.
  style.textContent = preview
    ? 'html, body { overflow: hidden !important; }'
    : 'html{scrollbar-width:thin;scrollbar-color:rgba(120,120,120,0.55) transparent}html::-webkit-scrollbar{width:4px;height:4px}html::-webkit-scrollbar-track{background:transparent}html::-webkit-scrollbar-thumb{border-radius:999px;background:rgba(120,120,120,0.55)}';
  document.head.append(style);
  return serializeDashboard(document);
};
